import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/utils/utils";
import { format } from "date-fns";
import { useMemo, useState } from "react";
import { Calendar } from "@/components/ui/calendar";
import { addTimezoneOffsetToUTCZeroTimeIfNeeded } from "../utils";
import { CalendarBlank } from "@phosphor-icons/react";

interface Props {
  selected?: Date;
  onSelect: (date: Date | undefined) => void;
  disabled?: boolean;
}

export const DatePicker = ({ selected, onSelect, disabled }: Props) => {
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);

  const handleOnSelect = (date: Date | undefined) => {
    onSelect(date);
    setIsPopoverOpen(false);
  };

  const adjustTimezoneDate = useMemo(() => {
    if (!selected) {
      return undefined;
    }
    /* we add UTC timezone offset so that when the date is parsed and selected for display, it shows the right date subtracting the timezone offset */
    const result = addTimezoneOffsetToUTCZeroTimeIfNeeded(selected);
    return result;
  }, [selected]);

  return (
    <Popover open={isPopoverOpen} onOpenChange={setIsPopoverOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          disabled={disabled}
          className={cn(
            "w-full justify-start border-none bg-[#1c1c1c] pl-3 text-left font-normal",
            !adjustTimezoneDate && "text-muted-foreground",
          )}
        >
          <CalendarBlank className="mr-2 size-4" />
          {adjustTimezoneDate ? (
            format(adjustTimezoneDate, "PPP")
          ) : (
            <span>Pick a date</span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <Calendar
          mode="single"
          selected={adjustTimezoneDate}
          onSelect={handleOnSelect}
          initialFocus
        />
      </PopoverContent>
    </Popover>
  );
};
